import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Auth } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Logo } from "../components/Logo";

export default function ChangePasswordPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (next.length < 8) return setErr("New password must be at least 8 characters");
    if (next !== confirm) return setErr("Passwords don't match");
    setBusy(true);
    try {
      await Auth.changePassword(current, next);
      await refresh();
      navigate("/livetv", { replace: true });
    } catch (e: unknown) {
      setErr(
        (e as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
          "Password change failed"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[420px] glass-panel rounded-xl p-8"
      >
        <div className="mb-1 flex justify-center">
          <Logo className="h-14 w-auto" />
        </div>
        <h2 className="text-[16px] font-ui font-semibold text-text-primary mb-1 mt-4 uppercase tracking-[0.04em] text-center">
          {user?.must_change_password ? "Set a new password" : "Change password"}
        </h2>
        <p className="text-[12px] font-ui text-text-secondary mb-6 text-center">
          {user?.must_change_password
            ? "First-time login: please choose a password."
            : `Logged in as ${user?.username}.`}
        </p>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Current password" type="password" value={current} onChange={setCurrent} />
          <Field label="New password" type="password" value={next} onChange={setNext} />
          <Field label="Confirm new password" type="password" value={confirm} onChange={setConfirm} />
          {err && <p className="text-[12px] font-ui text-danger">{err}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full h-12 bg-accent-gold text-canvas rounded-xl text-[13px] font-ui font-medium uppercase tracking-[0.04em] hover:bg-accent-gold-light transition-fast disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-[12px] font-ui font-medium uppercase tracking-[0.04em] text-text-secondary mb-1.5">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 bg-surface border border-muted rounded-lg px-4 font-ui text-[15px] text-text-primary gold-focus transition-fast"
      />
    </div>
  );
}
